/**
 * Real-Time WebSocket Hook for AkountFly
 * 
 * Provides real-time updates for transactions, activities, and dashboard metrics
 * using Socket.IO connection to the backend.
 * 
 * Usage:
 *   const { connected, transactions, subscribe, unsubscribe } = useRealtime();
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import { API } from '../App';
import { getCsrfToken } from '../utils/apiClient';

// Event types that map to backend RealtimeEvent enum
export const RealtimeEventTypes = {
  // Accounting events
  JOURNAL_POSTED: 'journal_posted',
  JOURNAL_VOIDED: 'journal_voided',
  
  // Document events
  INVOICE_CREATED: 'invoice_created',
  INVOICE_UPDATED: 'invoice_updated',
  INVOICE_DELETED: 'invoice_deleted',
  INVOICE_PAID: 'invoice_paid',
  
  BILL_CREATED: 'bill_created',
  BILL_UPDATED: 'bill_updated',
  BILL_DELETED: 'bill_deleted',
  BILL_PAID: 'bill_paid',
  
  // Payment events
  PAYMENT_RECORDED: 'payment_recorded',
  PAYMENT_VOIDED: 'payment_voided',
  
  // Bank feed events
  BANK_FEED_UPLOADED: 'bank_feed_uploaded',
  BANK_FEED_PROCESSED: 'bank_feed_processed',
  BANK_TRANSACTION_APPROVED: 'bank_transaction_approved',
  
  // Dashboard events
  DASHBOARD_METRIC_UPDATED: 'dashboard_metric_updated',
  DASHBOARD_REFRESH: 'dashboard_refresh',
  
  // Activity events
  NEW_ACTIVITY: 'new_activity',
  TRANSACTION_CREATED: 'transaction_created',
};

// Transaction event types for filtering
const TRANSACTION_EVENTS = [
  RealtimeEventTypes.INVOICE_CREATED,
  RealtimeEventTypes.INVOICE_PAID,
  RealtimeEventTypes.BILL_CREATED,
  RealtimeEventTypes.BILL_PAID,
  RealtimeEventTypes.PAYMENT_RECORDED,
  RealtimeEventTypes.JOURNAL_POSTED,
  RealtimeEventTypes.BANK_TRANSACTION_APPROVED,
  RealtimeEventTypes.TRANSACTION_CREATED,
];

/**
 * Hook for real-time WebSocket connection
 */
export const useRealtime = () => {
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const socketRef = useRef(null);
  const eventHandlersRef = useRef(new Map());
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Initialize socket connection. Phase 3: there is no localStorage access
  // token to inject into the Socket.IO `auth` payload - httpOnly cookies
  // can't be read by JavaScript. Instead, we POST cookie-authed to
  // /api/auth/ws-token to mint a 60-second handshake JWT, hand it to the
  // socket, and discard it. The token is purpose-tagged `type=ws` on the
  // server so it can't be reused as a regular access token.
  useEffect(() => {
    // If the user isn't logged in at all (no CSRF cookie present), skip
    // the WS connection entirely so we don't 401-spam ws-token on the
    // landing/auth pages.
    if (!getCsrfToken()) {
      return;
    }

    let cancelled = false;
    let socket = null;

    (async () => {
      let wsToken;
      try {
        const { data } = await axios.post(`${API}/auth/ws-token`);
        wsToken = data?.token;
      } catch (err) {
        // 401 here just means the cookie session has expired or the user is
        // logging in for the first time on this page - silently skip; the
        // app-level interceptor will refresh/redirect if needed.
        if (err?.response?.status !== 401) {
          console.error('Failed to mint WS token:', err?.message);
        }
        return;
      }
      if (cancelled || !wsToken) return;

      const baseUrl = API.replace('/api', '');
      socket = io(baseUrl, {
        path: '/socket.io',
        auth: { token: wsToken },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: maxReconnectAttempts,
      });

      socketRef.current = socket;

      // Connection event handlers
      socket.on('connect', () => {
        console.log('WebSocket connected:', socket.id);
        setConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
      });

      socket.on('disconnect', (reason) => {
        console.log('WebSocket disconnected:', reason);
        setConnected(false);
      });

      socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error.message);
        setConnectionError(error.message);
        reconnectAttempts.current += 1;

        if (reconnectAttempts.current >= maxReconnectAttempts) {
          console.log('Max reconnection attempts reached');
          socket.disconnect();
        }
      });

      // Listen for accounting events (from RealtimeService.broadcast_event)
      socket.on('accounting_event', (event) => {
        console.log('Received accounting event:', event.type);

        // If it's a transaction-related event, add to recent transactions
        if (TRANSACTION_EVENTS.includes(event.type)) {
          const newTransaction = {
            id: event.data?.id || Date.now().toString(),
            type: event.type,
            description: event.data?.description || getEventDescription(event),
            amount: event.data?.amount || 0,
            counterparty: event.data?.counterparty || event.data?.vendor_name || event.data?.customer_name || 'N/A',
            status: 'completed',
            created_at: event.timestamp,
            data: event.data,
          };

          setRecentTransactions(prev => [newTransaction, ...prev].slice(0, 50));
        }

        // Trigger any registered handlers for this event type
        const handlers = eventHandlersRef.current.get(event.type);
        if (handlers) {
          handlers.forEach(handler => handler(event));
        }
      });

      // Listen for new activity events (from realtime.py log_activity)
      socket.on('new_activity', (activity) => {
        console.log('Received new activity:', activity.event_type);

        const newTransaction = {
          id: activity.id,
          type: activity.event_type,
          description: activity.document_title,
          amount: activity.metadata?.amount || 0,
          counterparty: activity.metadata?.counterparty || 'N/A',
          status: 'completed',
          created_at: activity.created_at,
          user_name: activity.user_name,
          document_type: activity.document_type,
          document_id: activity.document_id,
        };

        setRecentTransactions(prev => [newTransaction, ...prev].slice(0, 50));
      });
    })();

    return () => {
      cancelled = true;
      if (socket) {
        console.log('Cleaning up WebSocket connection');
        socket.disconnect();
      }
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to specific event types
  const subscribe = useCallback((eventType, handler) => {
    if (!eventHandlersRef.current.has(eventType)) {
      eventHandlersRef.current.set(eventType, new Set());
    }
    eventHandlersRef.current.get(eventType).add(handler);
    
    // Return unsubscribe function
    return () => {
      const handlers = eventHandlersRef.current.get(eventType);
      if (handlers) {
        handlers.delete(handler);
      }
    };
  }, []);

  // Unsubscribe from event type
  const unsubscribe = useCallback((eventType, handler) => {
    const handlers = eventHandlersRef.current.get(eventType);
    if (handlers) {
      handlers.delete(handler);
    }
  }, []);

  // Clear recent transactions
  const clearTransactions = useCallback(() => {
    setRecentTransactions([]);
  }, []);

  return {
    connected,
    connectionError,
    recentTransactions,
    subscribe,
    unsubscribe,
    clearTransactions,
    socket: socketRef.current,
  };
};

// Helper function to generate event description
function getEventDescription(event) {
  const descriptions = {
    [RealtimeEventTypes.INVOICE_CREATED]: 'New invoice created',
    [RealtimeEventTypes.INVOICE_PAID]: 'Invoice payment received',
    [RealtimeEventTypes.BILL_CREATED]: 'New bill recorded',
    [RealtimeEventTypes.BILL_PAID]: 'Bill payment made',
    [RealtimeEventTypes.PAYMENT_RECORDED]: 'Payment recorded',
    [RealtimeEventTypes.JOURNAL_POSTED]: 'Journal entry posted',
    [RealtimeEventTypes.BANK_TRANSACTION_APPROVED]: 'Bank transaction approved',
  };
  
  return descriptions[event.type] || `${event.type} event`;
}

// ==================== REALTIME CONTEXT & PROVIDER ====================

const RealtimeContext = createContext(null);

/**
 * RealtimeProvider - Wraps the app to provide WebSocket connection
 * 
 * This provider should be placed high in the component tree to ensure
 * all child components can access the real-time connection.
 */
export const RealtimeProvider = ({ children }) => {
  const realtime = useRealtime();
  
  return (
    <RealtimeContext.Provider value={realtime}>
      {children}
    </RealtimeContext.Provider>
  );
};

/**
 * useRealtimeContext - Hook to access the realtime context
 * 
 * Use this hook in components that need real-time updates.
 * Must be used within a RealtimeProvider.
 */
export const useRealtimeContext = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    console.warn('useRealtimeContext must be used within a RealtimeProvider');
    // Return a no-op object to prevent crashes
    return {
      connected: false,
      connectionError: null,
      recentTransactions: [],
      subscribe: () => () => {},
      unsubscribe: () => {},
      clearTransactions: () => {},
      socket: null,
    };
  }
  return context;
};

export default useRealtime;
